import { useMemo, useState, type MouseEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../shared/auth/AuthContext';
import { apiClient } from '../../shared/api/client';
import { queryKeys } from '../../shared/api/query-keys';
import { formatCurrency, formatDate } from '../../shared/utils/format';
import { useCustomerLookup } from '../../shared/lookups/useParties';
import {
  Button,
  Table,
  Card,
  Badge,
  Input,
  Select,
  useToast,
} from '@nexora/design-system';

interface SalesOrder {
  id: string;
  customerId: string;
  /* Resolvido no servidor pela porta de nomes do Master Data. `null` quando o
     parceiro nao existe mais — a tela cai para o id abreviado. */
  customerName: string | null;
  status: string;
  grandTotal: number;
  /** Texto livre do pedido — é o que a referência chama de "Reference". */
  conditions: string;
  fiscalDocumentId: string | null;
  createdAt: string;
}

interface StatusAggregate {
  status: string;
  count: number;
  total: number;
}

interface OrdersSummary {
  byStatus: StatusAggregate[];
  activeCount: number;
  activeTotal: number;
  averageTicket: number;
}

interface PaginatedOrders {
  data: SalesOrder[];
  total: number;
  page: number;
  limit: number;
  /* Agregado da operação inteira, não da página. Somar as 20 linhas devolvidas
     e chamar de "valor total" seria número de página com nome de total. */
  counts: OrdersSummary;
}

const STATUS_VARIANT: Record<string, 'info' | 'warning' | 'success' | 'error' | 'neutral'> = {
  DRAFT: 'neutral',
  CONFIRMED: 'info',
  INVOICED: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'error',
};

const STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Rascunho',
  CONFIRMED: 'Confirmado',
  INVOICED: 'Faturado',
  COMPLETED: 'Concluído',
  CANCELLED: 'Cancelado',
};

const STATUS_ORDER = ['DRAFT', 'CONFIRMED', 'INVOICED', 'COMPLETED', 'CANCELLED'];

export function SalesListPage(): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const [customerSearch, setCustomerSearch] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [page, setPage] = useState(0);
  const [invoicingId, setInvoicingId] = useState<string | null>(null);
  const [fiscalDocumentId, setFiscalDocumentId] = useState('');
  const pageSize = 20;

  /* `GET /orders` filtra por `customerId`, não por nome. A tela pedia o UUID
     digitado ("Buscar por ID do cliente…"), o que ninguém tem à mão: agora a
     busca é por nome contra o cadastro de parceiros e o que vai ao servidor é
     o id do cliente escolhido. */
  const customers = useCustomerLookup(customerId ? '' : customerSearch);

  const { data, isLoading, error } = useQuery({
    queryKey: [...queryKeys.sales.list(tenantId), customerId, statusFilter, page],
    // Contrato real: GET /api/v1/orders (tenant vem da sessão, SDD §7.1)
    queryFn: async () =>
      apiClient.get<PaginatedOrders>('/orders', {
        params: {
          ...(statusFilter !== 'ALL' ? { status: statusFilter } : {}),
          ...(customerId ? { customerId } : {}),
          page: String(page + 1),
          limit: String(pageSize),
        },
      }),
    enabled: !!tenantId,
  });

  /* O lookup serve apenas ao filtro: escolher o cliente por nome e mandar o
     id ao servidor. O nome exibido em cada linha vem de `customerName`. */
  const sugestoes = useMemo(() => customers.data?.data ?? [], [customers.data]);
  const nomeDoFiltro = useMemo(
    () => sugestoes.find((c) => c.id === customerId)?.name ?? '',
    [sugestoes, customerId],
  );

  const counts = data?.counts;
  const byStatus = useMemo(() => {
    const map = new Map((counts?.byStatus ?? []).map((r) => [r.status, r]));
    return STATUS_ORDER.map((status) => ({
      status,
      count: map.get(status)?.count ?? 0,
      total: map.get(status)?.total ?? 0,
    })).filter((r) => r.count > 0);
  }, [counts]);

  const totalPedidos = byStatus.reduce((sum, r) => sum + r.count, 0);

  const invoiceMutation = useMutation({
    mutationFn: ({ id, docId }: { id: string; docId: string }) =>
      apiClient.patch<SalesOrder>(`/orders/${id}/invoice`, {
        fiscalDocumentId: docId,
      }),
    onSuccess: () => {
      addToast('Pedido faturado', 'success');
      setInvoicingId(null);
      setFiscalDocumentId('');
      queryClient.invalidateQueries({ queryKey: queryKeys.sales.list(tenantId) }).catch(console.error);
    },
    onError: (err) => {
      addToast((err as Error).message ?? 'Erro ao faturar pedido', 'error');
    },
  });

  const handleInvoiceConfirm = (): void => {
    if (!invoicingId || !fiscalDocumentId.trim()) return;
    invoiceMutation.mutate({ id: invoicingId, docId: fiscalDocumentId.trim() });
  };

  const columns = [
    {
      /* A referencia mostra "ORD-2023-8891". O `number` existe no schema
         (`sales.prisma:75`) mas nao chega a entidade nem ao DTO — o
         repositorio esta desalinhado do schema (backend todo-review6). Ate lá
         a coluna e o identificador, dito como identificador: abreviado na
         celula, completo no title, e nao chamado de "numero do pedido". */
      header: 'Identificador',
      accessor: 'id' as const,
      render: (value: string) => (
        <span className="so-mono" title={value}>
          {value.slice(0, 8)}
        </span>
      ),
      sortable: true,
    },
    {
      header: 'Data',
      accessor: 'createdAt' as const,
      render: (value: string) => <span className="so-nowrap">{formatDate(value)}</span>,
      sortable: true,
    },
    {
      header: 'Cliente',
      accessor: 'customerName' as const,
      render: (value: string | null, row: SalesOrder) =>
        value ?? <span className="so-mono">{row.customerId.slice(0, 8)}</span>,
      sortable: true,
    },
    {
      header: 'Referência',
      accessor: 'conditions' as const,
      render: (value: string) => value || '—',
    },
    {
      header: 'Valor',
      accessor: 'grandTotal' as const,
      render: (value: number) => <span className="so-num">{formatCurrency(value)}</span>,
      sortable: true,
    },
    {
      header: 'Status',
      accessor: 'status' as const,
      render: (value: string) => (
        <Badge variant={STATUS_VARIANT[value] ?? 'neutral'}>
          {STATUS_LABEL[value] ?? value}
        </Badge>
      ),
    },
    {
      header: 'NF-e',
      accessor: 'fiscalDocumentId' as const,
      render: (value: string | null) =>
        value ? <span className="so-mono">{value}</span> : <span className="so-muted">—</span>,
    },
    {
      header: 'Ações',
      accessor: 'id' as const,
      render: (value: string, row: SalesOrder) =>
        row.status === 'CONFIRMED' ? (
          <Button
            variant="outline"
            size="sm"
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              setInvoicingId(value);
              setFiscalDocumentId(row.fiscalDocumentId ?? '');
            }}
          >
            Faturar
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="sales-list-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Pedidos de Venda</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Pedidos de venda, faturamento e situação de cada um.
          </p>
        </div>
        <div className="flex flex-wrap gap-sm">
          <Button variant="primary" onClick={() => navigate({ to: '/sales/new' })}>
            Novo pedido
          </Button>
        </div>
      </div>

      {/* ── Indicadores ──────────────────────────────────────────────────
          A referência mostra "Lifetime Value", "Average Ticket" e a
          distribuição por situação. Os três vêm do agregado do servidor
          (`counts`), não da página de 20 linhas. */}
      <div className="so-bento">
        <Card>
          <div className="so-kpi">
            <span className="so-kpi__label">Valor dos pedidos ativos</span>
            <span className="so-kpi__value">{formatCurrency(counts?.activeTotal ?? 0)}</span>
            <span className="so-kpi__foot">
              {counts?.activeCount ?? 0} pedidos, sem contar cancelados
            </span>
          </div>
        </Card>

        <Card>
          <div className="so-kpi">
            <span className="so-kpi__label">Ticket médio</span>
            <span className="so-kpi__value">{formatCurrency(counts?.averageTicket ?? 0)}</span>
            <span className="so-kpi__foot">
              Sobre {counts?.activeCount ?? 0} {counts?.activeCount === 1 ? 'pedido' : 'pedidos'} ativos
            </span>
          </div>
        </Card>

        <Card>
          <div className="so-kpi">
            <span className="so-kpi__label">Distribuição por situação</span>
            {byStatus.length === 0 ? (
              <span className="so-kpi__foot">Nenhum pedido registrado.</span>
            ) : (
              <ul className="so-dist">
                {byStatus.map((r) => {
                  const pct = totalPedidos > 0 ? Math.round((r.count / totalPedidos) * 100) : 0;
                  return (
                    <li key={r.status}>
                      <div className="so-dist__head">
                        <span>{STATUS_LABEL[r.status] ?? r.status}</span>
                        <span className="so-dist__pct">{pct}%</span>
                      </div>
                      <div className="so-dist__bar">
                        <div
                          className={`so-dist__fill so-dist__fill--${r.status.toLowerCase()}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </Card>
      </div>

      <Card>
        <div className="filters">
          <div className="so-customer">
            <Input
              label=""
              placeholder="Buscar cliente por nome…"
              value={customerId ? (nomeDoFiltro || customerSearch) : customerSearch}
              onChange={(e) => {
                setCustomerSearch(e.target.value);
                setCustomerId('');
                setPage(0);
              }}
            />
            {!customerId && customerSearch.trim().length >= 2 && sugestoes.length > 0 && (
              <ul className="so-suggest">
                {sugestoes.slice(0, 8).map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setCustomerId(c.id);
                        setCustomerSearch(c.name);
                        setPage(0);
                      }}
                    >
                      {c.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {customerId && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setCustomerId('');
                setCustomerSearch('');
                setPage(0);
              }}
            >
              Limpar cliente
            </Button>
          )}

          <Select
            label=""
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
          >
            <option value="ALL">Todos os status</option>
            {STATUS_ORDER.map((status) => (
              <option key={status} value={status}>
                {STATUS_LABEL[status]}
              </option>
            ))}
          </Select>
        </div>

        {invoicingId ? (
          <div className="invoice-panel" role="dialog" aria-label="Faturar pedido">
            <Input
              label="ID do documento fiscal"
              placeholder="Ex.: NF-e 000123"
              value={fiscalDocumentId}
              onChange={(e) => setFiscalDocumentId(e.target.value)}
            />
            <div className="invoice-actions">
              <Button
                variant="outline"
                size="sm"
                disabled={invoiceMutation.isPending}
                onClick={() => {
                  setInvoicingId(null);
                  setFiscalDocumentId('');
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                size="sm"
                disabled={!fiscalDocumentId.trim() || invoiceMutation.isPending}
                loading={invoiceMutation.isPending}
                onClick={handleInvoiceConfirm}
              >
                Confirmar faturamento
              </Button>
            </div>
          </div>
        ) : null}

        <Table
          columns={columns}
          data={data?.data ?? []}
          isLoading={isLoading}
          error={error ? (error as Error).message : undefined}
          emptyMessage="Nenhum pedido de venda encontrado"
          onRowClick={(row) => navigate({ to: `/sales/${row.id}/edit` })}
          pagination={{
            page,
            pageSize,
            total: data?.total ?? 0,
            onPageChange: setPage,
          }}
        />
      </Card>

      <style>{`
        .sales-list-page { padding: 1.5rem; }

        .so-bento { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 1rem; margin-bottom: 1rem; }
        .so-kpi { display: flex; flex-direction: column; gap: 0.25rem; }
        .so-kpi__label { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 10px; letter-spacing: 0.04em; text-transform: uppercase; color: #43474e; }
        .so-kpi__value { font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; font-size: 1.375rem; font-weight: 600; color: #191c1e; }
        .so-kpi__foot { font-size: 0.75rem; color: #74777f; }

        .so-dist { list-style: none; margin: 0.25rem 0 0; padding: 0; display: flex; flex-direction: column; gap: 0.375rem; }
        .so-dist__head { display: flex; justify-content: space-between; font-size: 0.75rem; color: #43474e; }
        .so-dist__pct { font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; }
        .so-dist__bar { height: 5px; background: #e0e3e5; border-radius: 2px; overflow: hidden; }
        .so-dist__fill { height: 100%; background: #002045; }
        .so-dist__fill--draft { background: #74777f; }
        .so-dist__fill--confirmed { background: #002045; }
        .so-dist__fill--invoiced { background: #b8860b; }
        .so-dist__fill--completed { background: #1b7f4d; }
        .so-dist__fill--cancelled { background: #ba1a1a; }

        .filters { display: flex; gap: 1rem; margin-bottom: 1rem; align-items: flex-end; }
        .filters > *:first-child { flex: 1; }
        .so-customer { position: relative; flex: 1; }
        .so-suggest { position: absolute; z-index: 20; top: 100%; left: 0; right: 0; margin: 2px 0 0; padding: 0; list-style: none; background: #ffffff; border: 1px solid #c4c6cf; border-radius: 4px; box-shadow: 0 6px 16px rgba(0,0,0,0.12); max-height: 240px; overflow-y: auto; }
        .so-suggest button { display: block; width: 100%; text-align: left; padding: 0.5rem 0.75rem; background: none; border: 0; font-size: 0.875rem; color: #191c1e; cursor: pointer; }
        .so-suggest button:hover { background: #f2f4f6; }

        .so-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; }
        .so-num { font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; }
        .so-nowrap { white-space: nowrap; }
        .so-muted { color: #74777f; }

        .invoice-panel { display: flex; gap: 1rem; align-items: end; padding: 1rem; margin-bottom: 1rem; background: #f2f4f6; border: 1px solid #e0e3e5; border-radius: 4px; }
        .invoice-panel > *:first-child { flex: 1; }
        .invoice-actions { display: flex; gap: 0.5rem; }
      `}</style>
    </div>
  );
}
